import React, { useContext, useMemo, useState } from "react";
import {
  Divider,
  Icon,
  makeStyles,
  Paper,
  Typography,
  Button,
  ClickAwayListener,
  Box,
  IconButton,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";

import UserImage from "./UserImage";
import { AuthContext } from "../../../../../core/providers/AuthContext";
import LanguageSelector from "../dropdown/LanguageSelector";
import ReactCountryFlag from "react-country-flag";
import { flagIConStart } from "../../../../../solutions/employee-store/layout/header/components/EmployeeStoreLogo";
import {
  formatPriceWithCountry,
  MAP_COUNTRY_CURRENCY,
} from "../../../../../solutions/employee-store/util/util-ems";
import useInfo from "../../../../../solutions/employee-store/store/hooks/useInfo";
import { isNil, toNumber } from "lodash";
import OpenEmsByDefaultSwitch from "../../../../../solutions/employee-store/components/OpenEmsByDefaultSwitch";
import { Skeleton } from "@material-ui/lab";

const useStyles = makeStyles((theme) => ({
  paperContainer: {
    width: 320,
    height: 200,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-evenly",
    padding: 15,
    boxShadow: "rgb(0 0 0 / 20%) 5px 2px 10px",
    [theme.breakpoints.down("xs")]: {
      width: 250,
      height: 175,
      padding: 10,
    },
  },
  paperContainerEms: {
    width: 375,
    height: "auto",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-evenly",
    padding: 15,
    boxShadow: "rgb(0 0 0 / 20%) 5px 2px 10px",
    [theme.breakpoints.down("xs")]: {
      width: 250,
      height: "auto",
      padding: 10,
    },
  },
  profileInfo: {
    width: "100%",
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    flexDirection: "row",
    overflow: "auto",
    marginBottom: 6,
  },
  profileInfoTexts: {
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    flexDirection: "column",
    padding: 10,
  },
  buttonLogoutRoot: {
    padding: 10,
    height: 30,
    marginTop: 12,
    backgroundColor: `${theme.palette.content.mainColor}`,
    "&:hover": {
      backgroundColor: `${theme.palette.content.mainColor}`,
      transform: "scale(1.05)",
    },
  },
  buttonLogoutLabel: {
    textTransform: "none",
    gap: 5,
  },
  logoutTypo: {
    color: "white",
  },
  profileUserNameTypo: {
    fontSize: "0.75rem",
    fontWeight: "700",
    lineHeight: "1.6",
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.65rem",
    },
  },
  toggleButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    textTransform: "none",
    width: "100%",
  },
  iconAndLabelBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 6,
  },
  currentMothContainer: {
    display: "flex",
    alignItems: "center",
  },
  customDivider: { height: "1px", marginTop: "12px" },
}));

const commonIconStyle = {
  width: "auto",
  height: "auto",
  padding: 1,
};

const ENABLE_LANG_SELCTOR_EMS = false;

const CustomDivider = ({ mt, mb }) => {
  const classes = useStyles();

  const calculatedStyles = useMemo(() => {
    const styles = {};
    if (!isNil(mt)) {
      Object.assign(styles, { marginTop: mt });
    }

    if (!isNil(mb)) {
      Object.assign(styles, { marginBottom: mb });
    }
    return styles;
  }, [mt, mb]);
  return (
    <Divider
      flexItem
      className={classes.customDivider}
      style={calculatedStyles}
    />
  );
};

export default function ProfilePopoverContent({
  setOpenLogoutConfirmation,
  setOpenProfilePop,
  isEms,
}) {
  const classes = useStyles();
  const { auth } = useContext(AuthContext);
  const { t } = useTranslation();
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);
  const info = useInfo();

  const toggleLanguageSelector = () => {
    setShowLanguageSelector(!showLanguageSelector);
  };

  const EndToogleIcon = () => {
    if (showLanguageSelector) {
      return <Icon className="fas fa-times" />;
    } else {
      return null;
    }
  };

  return (
    <ClickAwayListener onClickAway={() => setOpenProfilePop(false)}>
      <Paper
        className={isEms ? classes.paperContainerEms : classes.paperContainer}
      >
        <div className={classes.profileInfo}>
          <UserImage isProfile={true} />
          <div className={classes.profileInfoTexts}>
            <Typography className={classes.profileUserNameTypo}>
              {auth?.userName}{" "}
              {isEms ? (
                <ReactCountryFlag
                  svg
                  countryCode={auth?.emsCountry}
                  style={flagIConStart}
                />
              ) : null}
            </Typography>
            <Typography variant="subtitle1">{auth?.company?.name}</Typography>
            {isEms && (
              <Typography variant="subtitle1">
                {t("EMPLOYEE_STORE.COD_EMPLOYEE", {
                  code: auth?.emsClient,
                })}
              </Typography>
            )}
            {isEms && <CustomDivider />}
            {isEms && (
              <div className={classes.currentMothContainer}>
                {info?.loadingInfo === true ? (
                  <Skeleton
                    width={175}
                    height={25}
                    variant="text"
                    style={{ marginTop: 12 }}
                  />
                ) : (
                  <>
                    <Typography variant="subtitle1">
                      {t("EMPLOYEE_STORE.SPENT_ACTUAL_MONTH", {
                        amount: formatPriceWithCountry({
                          price: toNumber(info?.amountMonth),
                          country: auth?.emsCountry,
                        }),
                        currency: MAP_COUNTRY_CURRENCY?.[auth?.emsCountry],
                      })}
                    </Typography>
                    <IconButton onClick={() => info?.handleFetchInitialInfo()}>
                      <Icon
                        className="fas fa-sync"
                        fontSize="small"
                        style={commonIconStyle}
                      />
                    </IconButton>
                  </>
                )}
              </div>
            )}
            {isEms && <CustomDivider mt={0} mb={5} />}
            {isEms ? <OpenEmsByDefaultSwitch /> : null}
          </div>
        </div>
        <Divider />
        {isEms && ENABLE_LANG_SELCTOR_EMS && (
          <>
            <Box mt={1} mb={1}>
              <Button
                onClick={toggleLanguageSelector}
                className={classes.toggleButton}
              >
                <Box className={classes.iconAndLabelBox}>
                  <Icon className="fas fa-flag" />
                  {t("DD_SETTINGS_LANGS")}
                </Box>

                <EndToogleIcon />
              </Button>
              {showLanguageSelector && (
                <Box mt={1}>
                  <LanguageSelector isEms={true} />
                </Box>
              )}
            </Box>
            <Divider />
          </>
        )}
        <Button
          classes={{
            root: classes.buttonLogoutRoot,
            label: classes.buttonLogoutLabel,
          }}
          onClick={() => {
            setOpenLogoutConfirmation(true);
          }}
        >
          <Icon className="fas fa-sign-out-alt" style={{ color: "white" }} />
          <Typography className={classes.logoutTypo}>
            {t("TOOLTIP_LOGOUT")}
          </Typography>
        </Button>
      </Paper>
    </ClickAwayListener>
  );
}
